import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Paper,
  Typography,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Menu,
  MenuItem,
  Badge,
  Tooltip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  Event as EventIcon,
  AccessTime as AccessTimeIcon,
  LocalHospital as LocalHospitalIcon,
  Notes as NotesIcon,
  Loop as LoopIcon,
  Check as CheckIcon,
  MoreVert as MoreVertIcon,
  MedicalServices as MedicalServicesIcon,
} from '@mui/icons-material';
import AppointmentService from '../../services/AppointmentService.js';

const AppointmentDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [appointment, setAppointment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isRecurring, setIsRecurring] = useState(false);
  const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);
  const [recurringUpdateDialogOpen, setRecurringUpdateDialogOpen] = useState(false);
  const [updateEntireSeries, setUpdateEntireSeries] = useState(false);
  
  useEffect(() => {
    const fetchAppointmentData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch appointment data from API
        const response = await AppointmentService.getAppointmentById(id);
        setAppointment(response.data);
        
        // Check if appointment is part of a recurring series
        const isRecurringResponse = await AppointmentService.isRecurringAppointment(id);
        setIsRecurring(isRecurringResponse);
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching appointment data:', error);
        setError('Failed to load appointment data. Please try again later.');
        setLoading(false);
      }
    };
    
    fetchAppointmentData();
  }, [id]);
  
  const handleEditAppointment = () => {
    navigate(`/appointments/${id}/edit`);
  };
  
  const handleDeleteDialogOpen = () => {
    setDeleteDialogOpen(true);
  };
  
  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
  };
  
  const handleDeleteAppointment = async () => {
    try {
      await AppointmentService.deleteAppointment(id);
      handleDeleteDialogClose();
      navigate('/appointments');
    } catch (error) {
      console.error('Error deleting appointment:', error);
      alert('Failed to delete appointment. Please try again.');
      handleDeleteDialogClose();
    }
  };
  
  const handleStatusMenuOpen = (event) => {
    setStatusMenuAnchor(event.currentTarget);
  };
  
  const handleStatusMenuClose = () => {
    setStatusMenuAnchor(null);
  };
  
  const handleStatusChange = async (newStatus) => {
    handleStatusMenuClose();
    
    if (isRecurring && newStatus === 'Confirmed') {
      setRecurringUpdateDialogOpen(true);
      return;
    }
    
    updateAppointmentStatus(newStatus, false);
  };
  
  const handleRecurringUpdateDialogClose = () => {
    setRecurringUpdateDialogOpen(false);
    setUpdateEntireSeries(false);
  };
  
  const handleRecurringStatusUpdate = () => {
    updateAppointmentStatus('Confirmed', updateEntireSeries);
    handleRecurringUpdateDialogClose();
  };
  
  const updateAppointmentStatus = async (newStatus, updateSeries) => {
    try {
      setStatusUpdateLoading(true);
      
      if (isRecurring) {
        await AppointmentService.updateRecurringAppointmentStatus(id, newStatus, updateSeries);
      } else {
        await AppointmentService.updateAppointmentStatus(id, newStatus);
      }
      
      // Update local state
      setAppointment(prev => ({
        ...prev,
        status: newStatus,
        updatedAt: new Date().toISOString()
      }));
      
      setStatusUpdateLoading(false);
    } catch (error) {
      console.error('Error updating appointment status:', error);
      alert('Failed to update appointment status. Please try again.');
      setStatusUpdateLoading(false);
    }
  };
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'Scheduled':
        return 'primary';
      case 'Completed':
        return 'success';
      case 'Cancelled':
        return 'error';
      default:
        return 'default';
    }
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error" variant="h6">{error}</Typography>
        <Button variant="contained" sx={{ mt: 2 }} onClick={() => window.location.reload()}>
          Retry
        </Button>
      </Box>
    );
  }
  
  if (!appointment) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6">Appointment not found</Typography>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          sx={{ mt: 2 }}
          component={RouterLink}
          to="/appointments"
        >
          Back to Appointments
        </Button>
      </Box>
    );
  }
  
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            sx={{ mr: 1 }}
            component={RouterLink}
            to="/appointments"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            Appointment Details
          </Typography>
        </Box>
        <Box>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            sx={{ mr: 1 }}
            onClick={handleEditAppointment}
          >
            Edit
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDeleteDialogOpen}
          >
            Delete
          </Button>
        </Box>
      </Box>
      
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="h5">
                    {appointment.type}
                  </Typography>
                  {isRecurring && (
                    <Tooltip title="Recurring Appointment">
                      <LoopIcon 
                        color="primary" 
                        sx={{ ml: 1, color: '#ff8c00' }} 
                      />
                    </Tooltip>
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Chip 
                    label={appointment.status}
                    color={getStatusColor(appointment.status)}
                    sx={{ mr: 1 }}
                  />
                  <IconButton 
                    size="small" 
                    onClick={handleStatusMenuOpen}
                    disabled={statusUpdateLoading}
                  >
                    {statusUpdateLoading ? (
                      <CircularProgress size={24} />
                    ) : (
                      <MoreVertIcon />
                    )}
                  </IconButton>
                  <Menu
                    anchorEl={statusMenuAnchor}
                    open={Boolean(statusMenuAnchor)}
                    onClose={handleStatusMenuClose}
                  >
                    <MenuItem onClick={() => handleStatusChange('Scheduled')}>Scheduled</MenuItem>
                    <MenuItem onClick={() => handleStatusChange('Confirmed')}>
                      <CheckIcon fontSize="small" sx={{ mr: 1 }} />
                      Confirmed
                    </MenuItem>
                    <MenuItem onClick={() => handleStatusChange('Completed')}>Completed</MenuItem>
                    <MenuItem onClick={() => handleStatusChange('Cancelled')}>Cancelled</MenuItem>
                  </Menu>
                </Box>
              </Box>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Patient
                  </Typography>
                  <Typography variant="body1">
                    <Button 
                      component={RouterLink} 
                      to={`/patients/${appointment.patientId}`}
                      sx={{ p: 0, textTransform: 'none' }}
                    >
                      {appointment.patient ? `${appointment.patient.firstName} ${appointment.patient.lastName}` : 'Unknown Patient'}
                    </Button>
                  </Typography>
                </Box>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LocalHospitalIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Physician
                  </Typography>
                  <Typography variant="body1">
                    {appointment.doctor ? `${appointment.doctor.firstName} ${appointment.doctor.lastName}` : 'Unknown Physician'}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <EventIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Date
                  </Typography>
                  <Typography variant="body1">
                    {new Date(appointment.appointmentDate || appointment.date).toLocaleDateString()}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <AccessTimeIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Time
                </Typography>
                <Typography variant="body1">
                  {appointment.startTime || appointment.time}
                </Typography>
              </Box>
            </Box>
          </Grid>

          {appointment.isHouseCall && (
            <>
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6">House Call Location</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body1">
                  {appointment.serviceAddress}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {`${appointment.serviceCity || ''}${appointment.serviceCity ? ', ' : ''}${appointment.serviceState || ''} ${appointment.servicePostalCode || ''}`}
                </Typography>
                {(() => {
                  const lat = appointment.serviceLatitude;
                  const lon = appointment.serviceLongitude;
                  const hasCoords = typeof lat === 'number' && typeof lon === 'number';
                  const addrString = [appointment.serviceAddress, appointment.serviceCity, appointment.serviceState, appointment.servicePostalCode]
                    .filter(Boolean)
                    .join(' ');
                  if (hasCoords) {
                    const bbox = `${(lon-0.01).toFixed(5)},${(lat-0.01).toFixed(5)},${(lon+0.01).toFixed(5)},${(lat+0.01).toFixed(5)}`;
                    const osmUrl = `https://www.openstreetmap.org/export/embed.html?layer=mapnik&marker=${lat},${lon}&bbox=${bbox}`;
                    return (
                      <Box sx={{ mt: 2 }}>
                        <Box sx={{ border: '1px solid #ddd', borderRadius: 1, overflow: 'hidden' }}>
                          <iframe title="map" src={osmUrl} width="100%" height="260" style={{ border: 0 }} />
                        </Box>
                        {addrString && (
                          <Box sx={{ mt: 1 }}>
                            <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addrString)}`} target="_blank" rel="noopener noreferrer">
                              Open in Google Maps
                            </a>
                          </Box>
                        )}
                      </Box>
                    );
                  }
                  if (addrString) {
                    const googleUrl = `https://maps.google.com/maps?q=${encodeURIComponent(addrString)}&output=embed`;
                    return (
                      <Box sx={{ mt: 2 }}>
                        <Box sx={{ border: '1px solid #ddd', borderRadius: 1, overflow: 'hidden' }}>
                          <iframe title="map" src={googleUrl} width="100%" height="260" style={{ border: 0 }} />
                        </Box>
                        <Box sx={{ mt: 1 }}>
                          <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addrString)}`} target="_blank" rel="noopener noreferrer">
                            Open in Google Maps
                          </a>
                        </Box>
                      </Box>
                    );
                  }
                  return null;
                })()}
              </Grid>
            </>
          )}
            
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                <NotesIcon sx={{ mr: 1, mt: 0.5, color: 'primary.main' }} />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Notes
                  </Typography>
                  <Typography variant="body1">
                    {appointment.notes || 'No notes available'}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      
      {/* Medical Records Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
              <MedicalServicesIcon sx={{ mr: 1, color: 'primary.main' }} />
              Medical Records
            </Typography>
            <Box>
              <Button
                variant="contained"
                startIcon={<MedicalServicesIcon />}
                component={RouterLink}
                to={`/medical-records/new?patientId=${appointment.patientId}&appointmentId=${appointment.id}&physicianId=${appointment.physicianId || appointment.doctorId}`}
                sx={{ mr: 1 }}
              >
                Create Medical Record
              </Button>
              <Button
                variant="outlined"
                component={RouterLink}
                to={`/medical-records?patientId=${appointment.patientId}`}
              >
                View Patient Records
              </Button>
            </Box>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Create a new medical record for this appointment or view existing records for {appointment.patientName}.
          </Typography>
        </CardContent>
      </Card>
      
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Typography variant="caption" color="text.secondary">
          Created: {new Date(appointment.createdAt).toLocaleString()}
          {appointment.updatedAt !== appointment.createdAt && 
            ` | Last updated: ${new Date(appointment.updatedAt).toLocaleString()}`}
        </Typography>
      </Box>
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
      >
        <DialogTitle>Delete Appointment</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this appointment? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose}>Cancel</Button>
          <Button onClick={handleDeleteAppointment} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Recurring Appointment Update Dialog */}
      <Dialog
        open={recurringUpdateDialogOpen}
        onClose={handleRecurringUpdateDialogClose}
      >
        <DialogTitle>Update Recurring Appointment</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This is part of a recurring appointment series. Would you like to update just this appointment or the entire series?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRecurringUpdateDialogClose}>Cancel</Button>
          <Button onClick={() => {
            setUpdateEntireSeries(false);
            handleRecurringStatusUpdate();
          }}>
            This Appointment Only
          </Button>
          <Button onClick={() => {
            setUpdateEntireSeries(true);
            handleRecurringStatusUpdate();
          }} color="primary" autoFocus>
            Entire Series
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AppointmentDetails;